import type { BossResponse, DaresResponse } from '@shared/protocol/messages';
import type { App } from '../app/App';
import type { Topic } from '../app/GameStore';
import { claimBossPrize, syncBoss, syncDares } from '../game/systems/community';
import type { Monster, SaveData } from '../game/systems/model';
import { t } from '../i18n';

/** A fingerprint of the server monsters of one source, so unchanged data does not re-render pages. */
function fingerprint(save: SaveData, source: Monster['source']): string {
  return JSON.stringify(save.monsters.filter((m) => m.source === source).map((m) => [m.id, m.name, m.hp, m.atk, m.def, m.luck, m.author, m.drawing?.strokes.length]));
}

/**
 * Applies fn to a copy-check of the save: the store (and the pages listening
 * to `topics`) is only updated when the monsters of `source` changed or fn
 * reports another change.
 */
function syncIfChanged(app: App, source: Monster['source'], topics: Topic[], fn: (s: SaveData) => boolean): void {
  const before = fingerprint(app.store.state, source);
  const probe = structuredClone(app.store.state);
  const other = fn(probe);
  if (!other && fingerprint(probe, source) === before) return;
  app.store.update(topics, (s) => void fn(s));
}

/** Pending incoming dares not fought yet (shown on the Friends tab badge). */
export const unansweredDares = (res: DaresResponse): number => res.incoming.filter((d) => d.attempts === 0).length;

/** Fetches my dares and tapes the ones friends sent me into the notebook. Null when offline. */
export async function refreshDares(app: App): Promise<DaresResponse | null> {
  const online = app.store.state.online;
  if (!online) return null;
  app.api.token = online.token;
  try {
    const res = await app.api.dares();
    syncIfChanged(app, 'dare', ['monsters'], (s) => (syncDares(s, res.incoming), false));
    return res;
  } catch {
    return null;
  }
}

/** Fetches the boss of the week, keeps the notebook's boss page current and pays the prize. Null when offline. */
export async function refreshBoss(app: App): Promise<BossResponse | null> {
  const online = app.store.state.online;
  if (!online) return null;
  app.api.token = online.token;
  try {
    const res = await app.api.boss();
    let prize = 0;
    syncIfChanged(app, 'boss', ['monsters', 'inventory', 'memories'], (s) => {
      syncBoss(s, res);
      prize = claimBossPrize(s, res);
      return prize > 0;
    });
    if (prize) app.toast(t('The boss is down! Everyone who helped gets +{n} coins.', { n: prize }), 'success');
    return res;
  } catch {
    return null;
  }
}
